import { Loading } from "src/components/utils/Loading.tsx";
import { ScoreDetailContextProvider } from "src/context/ScoreDetail/provider.tsx";
import { URLHandler } from "src/services/URLHandler";
import { useLocation } from "react-router";
import { useIngredientContext } from "./useIngredientContext";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { OlapParamsService } from "src/context/OlapScores/OlapParamsService";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useGetIngredientScores } from "src/api/client-api/olapScores/useGetIngredientScores";
import { getNumberOfSuppliers } from "./getNumberOfSuppliers";
import { useGetOnePost } from "src/api/client-api/post/useGetOnePost";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { Dimensions } from "src/context/OlapScores/useDimensions";

type IngredientScoreContextProviderProps = {
    children: ReactNode | ReactNode[];
    companyId: number;
    ingredientId: number;
    scoreId: number;
};
export const IngredientScoreContextProvider = (props: IngredientScoreContextProviderProps) => {
    const { children, companyId, ingredientId, scoreId } = props;
    const location = useLocation();

    // Extraire l'URL de retour depuis les paramètres pour la propager
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");

    const scoreDetailDimensions: Dimensions = [
        [["headings"], ["subheadings"], ["properties"], ["subproperties"], ["multivalues"]],
        [["ssq_versions"], ["headings"], ["subheadings"], ["properties"], ["subproperties"], ["multivalues"]],
    ];
    const initialDimensions = OlapParamsService.getInitialDimensions(scoreDetailDimensions);

    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const { ingredient, ssqVersion, isSsqVersionReady, posts, answerableSupplierId, readableSupplierIds } =
        useIngredientContext();
    const { scoresWithVariations, selectedVariations, getEntityScore } = useEntityScores();

    const score = getEntityScore(scoreId);
    const numberOfSuppliers = getNumberOfSuppliers(ingredient?.suppliers);

    const scoresParams = useOlapScoresParams({
        scores: {
            ids: [scoreId],
            dimensions: initialDimensions,
            variations: selectedVariations.query,
        },
        prevScores: true,
    });

    const {
        data: scores,
        isLoading,
        isRefetching,
    } = useGetIngredientScores({
        ingredientId,
        campaignId: currentCampaign?.id,
        companyId: selectedCompany?.id,
        scoresQuery: scoresParams.query,
    });

    const { post: firstPost, refetch: fetchPostForAnswers } = useGetOnePost({
        postId: posts?.[0]?.id,
        enabled: readableSupplierIds.length > 0,
    });
    const { ssqVersion: firstPostSsqVersion } = useGetOneSsqVersion(firstPost?.ssqVersionId);
    const localizedLabelSsqVersion = ssqVersion ?? firstPostSsqVersion;

    if (!selectedCompany || ingredient === undefined || score === undefined) {
        return <Loading />;
    }

    const scoreWithVariations = scoresWithVariations?.filter(
        (scoreWithVariation) => scoreWithVariation.scoreId === scoreId,
    );

    return (
        <ScoreDetailContextProvider
            score={score}
            olapScores={scores?.olapScores[scoreId]}
            olapPrevScores={scores?.olapPrevScores?.[scoreId]}
            scoresParams={scoresParams}
            scoreDetailDimensions={scoreDetailDimensions}
            ignoredDrillDownDimensions={["multivalues"]}
            olapLocalizedLabelContext={{ ssqVersion: localizedLabelSsqVersion }}
            fetchPostForAnswers={
                numberOfSuppliers === 1 && answerableSupplierId !== null ? fetchPostForAnswers : undefined
            }
            selectedVariations={selectedVariations}
            scoreWithVariations={scoreWithVariations}
            isLoading={isLoading || isRefetching || !isSsqVersionReady}
            prevCampaignLabel={scores?.prevCampaignName}
            goBackLink={
                returnUrl ?
                    `${URLHandler.ingredient.scores(companyId, ingredientId)}?returnUrl=${encodeURIComponent(returnUrl)}`
                :   URLHandler.ingredient.scores(companyId, ingredientId)
            }
            tabConfig={{
                distribution: {
                    link: (scoreId) => {
                        const distUrl = URLHandler.ingredient.scoreDistribution(
                            companyId,
                            ingredientId,
                            scoreId.toString(),
                        );
                        return returnUrl ? `${distUrl}?returnUrl=${encodeURIComponent(returnUrl)}` : distUrl;
                    },
                },
            }}
        >
            {children}
        </ScoreDetailContextProvider>
    );
};
